import { FC, ReactNode } from 'react';
import type {
  RemediationScopeGenerateItem,
  RemediationScopeRemediateItem,
} from '@libs/types';
import { Box, Checkbox, Chip, Link, Paper, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { RemediationContentKind } from '@libs/constants';
import { useTrans } from '@libs/i18n';
import { FilePlusIcon, type Icon as PhosphorIcon, PencilSimpleIcon } from '@phosphor-icons/react';

import { ScoreBar } from './ScoreBar';

/** Extension shown on the file tag. Real name wins; otherwise the content kind. */
function fileLabel(fileName: string | null, kind: RemediationContentKind): string {
  const ext = fileName?.split('.').pop();
  if (ext && ext.length <= 4 && ext !== fileName) return ext.toUpperCase();
  return kind === RemediationContentKind.SPREADSHEET ? 'XLSX' : 'PDF';
}

/**
 * A full name is several times longer than the key it replaced, so the chip
 * wraps to as many lines as it needs instead of running past its column.
 */
const CHIP_SX = {
  height: 'auto',
  maxWidth: '100%',
  py: 0.35,
  '& .MuiChip-label': {
    display: 'block',
    whiteSpace: 'normal',
    lineHeight: 1.35,
    overflowWrap: 'anywhere',
  },
} as const;

const TAG_COLOR: Record<string, string> = {
  PDF: '#D84A1B',
  DOCX: '#2178EF',
  DOC: '#2178EF',
  XLSX: '#1B9E5C',
  XLS: '#1B9E5C',
};

/** The coloured file-type square from the design. */
const FileTag: FC<{ label: string }> = observer(({ label }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        width: 26,
        height: 32,
        borderRadius: 1.5,
        flexShrink: 0,
        backgroundColor: TAG_COLOR[label] ?? theme.palette.text.secondary,
        color: theme.palette.common.white,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 8.5,
        fontWeight: 700,
        letterSpacing: '0.02em',
      }}
    >
      {label}
    </Box>
  );
});

/** Dashed placeholder square — this document does not exist yet. */
const GhostTag: FC = observer(() => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        width: 26,
        height: 32,
        borderRadius: 1.5,
        flexShrink: 0,
        border: `1.5px dashed ${theme.palette.grey[500]}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <FilePlusIcon size={15} color={theme.palette.text.tertiary} />
    </Box>
  );
});

interface SectionProps {
  icon: PhosphorIcon;
  iconBg: string;
  iconColor: string;
  title: string;
  subtitle: string;
  count: number;
  /** null hides the select-all control (nothing to select). */
  onToggleAll: (() => void) | null;
  allSelected: boolean;
  accentBackground?: string;
  accentBorder?: string;
  columns: string[];
  /** Grid template shared by the column header and every row. */
  gridTemplate: string;
  emptyTitle: string;
  emptyBody: string;
  children: ReactNode;
}

const SectionCard: FC<SectionProps> = observer(
  ({
    icon: IconComponent,
    iconBg,
    iconColor,
    title,
    subtitle,
    count,
    onToggleAll,
    allSelected,
    accentBackground,
    accentBorder,
    columns,
    gridTemplate,
    emptyTitle,
    emptyBody,
    children,
  }) => {
    const theme = useTheme();
    const { t } = useTrans();

    return (
      <Paper
        elevation={0}
        sx={{
          backgroundColor: accentBackground ?? theme.palette.background.paper,
          border: `1px solid ${accentBorder ?? theme.palette.grey[300]}`,
          borderRadius: 4,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.75,
            p: 2.25,
            borderBottom: `1px solid ${theme.palette.grey[300]}`,
          }}
        >
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 2.5,
              backgroundColor: iconBg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <IconComponent size={20} color={iconColor} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="textMD_medium">{title}</Typography>
              <Chip size="small" label={count} />
            </Box>
            <Typography variant="textXS_regular" sx={{ color: 'text.secondary', mt: 0.4 }}>
              {subtitle}
            </Typography>
          </Box>
          {onToggleAll && (
            <Link
              component="button"
              type="button"
              onClick={onToggleAll}
              sx={{
                flexShrink: 0,
                fontSize: 12.5,
                fontWeight: 600,
                color: iconColor,
                textDecoration: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              {t(allSelected ? 'app.remediation.scope.selectNone' : 'app.remediation.scope.selectAll')}
            </Link>
          )}
        </Box>

        {count === 0 ? (
          <Box sx={{ py: 5, px: 3, textAlign: 'center' }}>
            <Typography variant="textSM_semiBold" sx={{ display: 'block', mb: 0.75 }}>
              {emptyTitle}
            </Typography>
            <Typography
              variant="textSM_regular"
              sx={{ color: 'text.secondary', maxWidth: 460, mx: 'auto' }}
            >
              {emptyBody}
            </Typography>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: { xs: 'none', md: 'grid' },
                gridTemplateColumns: gridTemplate,
                gap: 1.75,
                px: 2.25,
                py: 1.15,
                backgroundColor: theme.palette.grey[200],
              }}
            >
              {columns.map((c, i) => (
                <Typography
                  key={i}
                  sx={{ fontSize: 11.5, fontWeight: 600, color: 'text.secondary' }}
                >
                  {c}
                </Typography>
              ))}
            </Box>
            {children}
          </>
        )}
      </Paper>
    );
  },
);

const Row: FC<{
  checked: boolean;
  onToggle: () => void;
  highlight: string;
  gridTemplate: string;
  children: ReactNode;
}> = observer(({ checked, onToggle, highlight, gridTemplate, children }) => {
  const theme = useTheme();
  return (
    <Box
      onClick={onToggle}
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '28px 1fr', md: gridTemplate },
        alignItems: 'center',
        gap: 1.75,
        px: 2.25,
        py: 1.5,
        cursor: 'pointer',
        borderBottom: `1px solid ${theme.palette.grey[300]}`,
        backgroundColor: checked ? highlight : 'transparent',
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      <Checkbox
        checked={checked}
        onClick={(e) => e.stopPropagation()}
        onChange={onToggle}
        size="small"
        sx={{ p: 0 }}
      />
      {children}
    </Box>
  );
});

const NameCell: FC<{ tag: ReactNode; name: string; caption: ReactNode }> = observer(
  ({ tag, name, caption }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
      {tag}
      <Box sx={{ minWidth: 0 }}>
        {/* `display: block` because the app's custom typography variants have no
            `variantMapping` and MUI renders them as <span>. Inline, the caption
            below ran on straight after the name — "Privacy NoticeWill be written
            by AI" — and `noWrap` could not truncate an inline box either. */}
        <Typography
          variant="textSM_regular"
          noWrap
          title={name}
          sx={{ display: 'block', fontWeight: 500 }}
        >
          {name}
        </Typography>
        {caption}
      </Box>
    </Box>
  ),
);

// ── Section A · uploads to improve ──────────────────────────────────────────

const REMEDIATE_GRID = '28px 1fr 150px 1fr';

export const RemediateSection: FC<{
  items: RemediationScopeRemediateItem[];
  selected: Set<string>;
  onToggle: (id: string) => void;
  onToggleAll: () => void;
}> = observer(({ items, selected, onToggle, onToggleAll }) => {
  const theme = useTheme();
  const { t } = useTrans();

  return (
    <SectionCard
      icon={PencilSimpleIcon}
      iconBg={theme.palette.blue[100]}
      iconColor={theme.palette.primary.main}
      title={t('app.remediation.scope.remediateTitle')}
      subtitle={t('app.remediation.scope.remediateHint')}
      count={items.length}
      onToggleAll={items.length > 0 ? onToggleAll : null}
      allSelected={items.length > 0 && items.every((i) => selected.has(i.sourceAssessmentDomainDocumentId))}
      gridTemplate={REMEDIATE_GRID}
      columns={[
        '',
        t('app.remediation.scope.colDocument'),
        t('app.remediation.scope.colScore'),
        t('app.remediation.scope.colDomain'),
      ]}
      emptyTitle={t('app.remediation.scope.remediateEmptyTitle')}
      emptyBody={t('app.remediation.scope.remediateEmptyBody')}
    >
      {items.map((item) => {
        const id = item.sourceAssessmentDomainDocumentId;
        const label = fileLabel(item.fileName, item.contentKind);
        return (
          <Row
            key={id}
            checked={selected.has(id)}
            onToggle={() => onToggle(id)}
            highlight={theme.palette.blue[100]}
            gridTemplate={REMEDIATE_GRID}
          >
            <NameCell
              tag={<FileTag label={label} />}
              name={item.fileName}
              caption={
                <Typography
                  sx={{
                    fontSize: 11,
                    color: 'text.tertiary',
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em',
                  }}
                >
                  {label}
                </Typography>
              }
            />
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <ScoreBar score={item.maturityPct} />
            </Box>
            {/* Names, not the catalog's keys: `OMs` and `PDPP` are identifiers
                the reviewer has no way to decode. The key stays as the tooltip,
                since it is what appears in exports and support threads. */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.75, flexWrap: 'wrap' }}>
              <Chip size="small" label={item.domainName} title={item.domainKey} sx={CHIP_SX} />
              {item.standardDocumentName && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={item.standardDocumentName}
                  title={item.standardDocumentKey ?? undefined}
                  sx={CHIP_SX}
                />
              )}
            </Box>
          </Row>
        );
      })}
    </SectionCard>
  );
});

// ── Section B · documents to create ─────────────────────────────────────────

const GENERATE_GRID = '28px 1fr 1fr 110px';

export const GenerateSection: FC<{
  items: RemediationScopeGenerateItem[];
  selected: Set<string>;
  onToggle: (id: string) => void;
  onToggleAll: () => void;
}> = observer(({ items, selected, onToggle, onToggleAll }) => {
  const theme = useTheme();
  const { t } = useTrans();

  return (
    <SectionCard
      icon={FilePlusIcon}
      iconBg={theme.palette.ocean[100]}
      iconColor={theme.palette.ocean[700]}
      title={t('app.remediation.scope.generateTitle')}
      subtitle={t('app.remediation.scope.generateHint')}
      count={items.length}
      onToggleAll={items.length > 0 ? onToggleAll : null}
      allSelected={items.length > 0 && items.every((i) => selected.has(i.domainStandardDocumentId))}
      accentBackground={theme.palette.ocean[100]}
      accentBorder={theme.palette.ocean[300]}
      gridTemplate={GENERATE_GRID}
      columns={[
        '',
        t('app.remediation.scope.colDocumentType'),
        t('app.remediation.scope.colDomain'),
        t('app.remediation.scope.colFormat'),
      ]}
      emptyTitle={t('app.remediation.scope.generateEmptyTitle')}
      emptyBody={t('app.remediation.scope.generateEmptyBody')}
    >
      {items.map((item) => {
        const id = item.domainStandardDocumentId;
        return (
          <Row
            key={id}
            checked={selected.has(id)}
            onToggle={() => onToggle(id)}
            highlight={theme.palette.ocean[200]}
            gridTemplate={GENERATE_GRID}
          >
            <NameCell
              tag={<GhostTag />}
              name={item.name}
              caption={
                <Typography
                  sx={{
                    fontSize: 11,
                    color: theme.palette.ocean[700],
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  {t('app.remediation.scope.willBeGenerated')}
                </Typography>
              }
            />
            {/* Domain only. The row's own title is this slot's document name, so
                a second chip repeating it would say the same thing twice. */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.75, flexWrap: 'wrap' }}>
              <Chip size="small" label={item.domainName} title={item.domainKey} sx={CHIP_SX} />
            </Box>
            <Typography
              variant="textXS_regular"
              sx={{ color: 'text.secondary', display: { xs: 'none', md: 'block' } }}
            >
              {fileLabel(null, item.contentKind)}
            </Typography>
          </Row>
        );
      })}
    </SectionCard>
  );
});
